/**
 * Tabs — React web implementation.
 *
 * Composes WebPressableSurface per tab (which owns the focus-visible ring,
 * delegated analytics, and `role="tab"`/`aria-selected` semantics) and adds
 * the composite-widget keyboard contract (ADR-025 Rule 1): one roving tab
 * stop across the tab list with ArrowLeft/ArrowRight/Home/End moving focus.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { PressableSurface } from '../PressableSurface/PressableSurface.web';
import { Text } from '../Text/Text.web';
import type { TabsProps } from './Tabs';
import { TABS_UNDERLINE_HEIGHT, TABS_WEB_DIVIDER_HEIGHT, tabsItemTestID } from './Tabs';

export interface WebTabsProps extends TabsProps {
  onChange: (key: string) => void;
  /** Web-only: every tab takes an equal share of the row (e.g. a two-way
   *  Phone / Email switch) instead of sizing to its label. */
  fullWidth?: boolean;
  'data-testid'?: string;
}

export function Tabs({
  items,
  activeKey,
  onChange,
  fullWidth = false,
  actionId,
  accessibilityLabel,
  testID,
  'data-testid': dataTestId,
}: WebTabsProps) {
  const rootTestId = dataTestId ?? testID;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeIndex = items.findIndex((item) => item.key === activeKey);
  const [focusIndex, setFocusIndex] = useState(activeIndex >= 0 ? activeIndex : 0);
  // Web-only pointer hover on an inactive tab previews it: darker label and a
  // light underline. The active tab has no hover look.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // Keep the roving tab stop valid as the host changes selection or the item
  // list after mount: prefer the active tab, else clamp into range so the
  // list never loses its single tab stop.
  useEffect(() => {
    setFocusIndex((prev) => {
      if (activeIndex >= 0) return activeIndex;
      if (items.length === 0) return 0;
      return Math.min(prev, items.length - 1);
    });
  }, [activeIndex, items.length]);

  // Automatic activation (WAI-ARIA tabs): the panels behind these tabs render
  // instantly, so keyboard movement both focuses and selects the tab. The
  // selection routes through the tab's own click path so the delegated
  // analytics listener observes keyboard changes exactly like pointer ones.
  const move = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      const clamped = (next + items.length) % items.length;
      setFocusIndex(clamped);
      refs.current[clamped]?.focus();
      refs.current[clamped]?.click();
    },
    [items.length],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        move(focusIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        move(focusIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(items.length - 1);
        break;
      default:
        break;
    }
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    // Equal-share tabs sit edge to edge so their underlines meet.
    gap: fullWidth ? 0 : spacing.lg,
    // Always overflow-capable: a fitting item set simply does not scroll.
    overflowX: 'auto',
  };

  const tabContentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // Explicit minimum pointer/touch bounds, mirroring the native tab; content
    // pins to the bottom so the underline stays flush against the divider.
    justifyContent: 'flex-end',
    minWidth: sizing.touchTarget.minimum,
    minHeight: sizing.touchTarget.minimum,
    gap: spacing.s,
  };

  const underlineStyle: React.CSSProperties = {
    height: TABS_UNDERLINE_HEIGHT,
    alignSelf: 'stretch',
    borderRadius: radius.rounded,
    backgroundColor: colors.icon.primary,
  };

  const underlineHiddenStyle: React.CSSProperties = {
    height: TABS_UNDERLINE_HEIGHT,
  };

  const underlineHoverStyle: React.CSSProperties = {
    ...underlineStyle,
    backgroundColor: colors.button.disabled,
  };

  const dividerStyle: React.CSSProperties = {
    height: TABS_WEB_DIVIDER_HEIGHT,
    backgroundColor: colors.stroke.divider,
  };

  return (
    <div role="tablist" aria-label={accessibilityLabel} data-testid={rootTestId}>
      <div style={rowStyle} onKeyDown={onKeyDown}>
        {items.map((item, index) => {
          const active = item.key === activeKey;
          const hovered = !active && item.key === hoveredKey;
          return (
            <div
              key={item.key}
              style={fullWidth ? { flex: '1 1 0%', minWidth: 0 } : { flex: 'none' }}
              onPointerEnter={(event) => {
                // Mouse only: a tap fires a pointer-enter with no matching leave.
                if (event.pointerType === 'mouse') setHoveredKey(item.key);
              }}
              onPointerLeave={() => setHoveredKey(null)}
            >
              <PressableSurface
                ref={(el) => {
                  refs.current[index] = el;
                }}
                actionId={actionId}
                elementKey={item.key}
                role="tab"
                onPress={active ? undefined : () => onChange(item.key)}
                selected={active}
                accessibilityLabel={item.label}
                tabIndex={index === focusIndex ? 0 : -1}
                testID={item.testID ?? tabsItemTestID(rootTestId, item.key)}
              >
                <span style={tabContentStyle}>
                  <Text
                    variant={active ? 'b2Bold' : 'b2Regular'}
                    color={active || hovered ? 'primary' : 'secondary'}
                    value={item.label}
                  />
                  <span
                    aria-hidden
                    style={
                      active ? underlineStyle : hovered ? underlineHoverStyle : underlineHiddenStyle
                    }
                  />
                </span>
              </PressableSurface>
            </div>
          );
        })}
      </div>
      <div aria-hidden style={dividerStyle} />
    </div>
  );
}
